import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ispPackageAPI, walletAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';

// ── Icons (same set as InternetPackages.jsx) ───────────────────────────────────
const Copy = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);
const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const fmt = n => Number(n || 0).toLocaleString();

// Dedicated purchase page — replaces the bottom-sheet that used to open from
// the /internet package grid. Reached only by tapping a package card from
// the already-authenticated /internet page, so this lives under the normal
// ProtectedRoute/AppLayout (no LinkPackage-style logged-out handling needed —
// there's no external-link distribution path for a specific package purchase).
export default function BuyPackage() {
  const { packageId } = useParams();
  const { user, updateUser } = useAuth();

  const [pkg, setPkg]           = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [balance, setBalance]   = useState(null);
  const [loading, setLoading]   = useState(true);

  const [phone, setPhone]             = useState('');
  const [ispIdentity, setIspIdentity] = useState('');
  const [buying, setBuying]           = useState(false);
  const [err, setErr]                 = useState('');
  const [result, setResult]           = useState(null); // { credential, purchase }
  const [copied, setCopied]           = useState(false);
  const [copiedField, setCopiedField] = useState(null); // 'username' | 'password' | 'both' | null

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [pkgRes, walRes] = await Promise.all([
        ispPackageAPI.list(),
        walletAPI.portfolio().catch(() => null),
      ]);
      const found = (pkgRes.packages || []).find(p => p._id === packageId);
      if (!found) { setNotFound(true); }
      else { setPkg(found); }
      if (walRes) {
        const ugx = (walRes.portfolio?.wallets || []).find(w => w.currency === 'UGX' && w.walletType === 'primary')
                 || (walRes.portfolio?.wallets || []).find(w => w.currency === 'UGX');
        setBalance(ugx ? ugx.balance : null);
      }
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Failed to load package');
    } finally {
      setLoading(false);
    }
  }, [packageId]);

  useEffect(() => { load(); }, [load]);

  const needsIspIdentity = pkg?.providerId === 'manual_activation' && !user?.ispIdentity;

  const handleBuy = async () => {
    if (!pkg) return;
    if (needsIspIdentity && !ispIdentity.trim()) {
      setErr('Enter your DWIFI username/ticket number to continue.');
      return;
    }
    setBuying(true);
    setErr('');
    try {
      const r = await ispPackageAPI.purchase(pkg._id, phone || undefined, ispIdentity || undefined);
      setResult({ credential: r.credential, purchase: r.purchase });
      if (needsIspIdentity) updateUser({ ispIdentity: ispIdentity.trim() });
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Purchase failed';
      setErr(msg);
      if (e.response?.data?.code === 'INSUFFICIENT_BALANCE') {
        const bal  = e.response.data.walletBalance;
        const need = e.response.data.required;
        setErr(`Insufficient balance. You have UGX ${fmt(bal)} but need UGX ${fmt(need)}. Please top up first.`);
      }
    } finally {
      setBuying(false);
    }
  };

  const copyCredential = () => {
    if (!result?.credential) return;
    navigator.clipboard.writeText(result.credential).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const copyField = (fieldKey, text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const styles = {
    page:      { maxWidth: 500, margin: '0 auto', padding: '0 0 80px' },
    hero:      { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
                 padding: '24px 20px 28px', color: '#fff' },
    heroTitle: { fontSize: 20, fontWeight: 700, margin: 0 },
    heroSub:   { fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 4 },
    body:      { padding: '20px' },
    label:     { fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 },
    input:     { width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
                 border: '1px solid var(--border)', background: 'var(--bg-page)',
                 color: 'var(--text-primary)', fontSize: 14, marginBottom: 12 },
    confirmBtn:(disabled) => ({ width: '100%', padding: '13px 0', borderRadius: 10,
                 background: disabled ? '#64748b' : '#22d3ee', color: disabled ? '#94a3b8' : '#0f172a',
                 fontWeight: 700, fontSize: 15, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer' }),
    backLink:  { display: 'block', textAlign: 'center', marginTop: 14, color: 'var(--text-muted)',
                 fontSize: 13, textDecoration: 'none' },
    errBox:    { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 },
    emptyMsg:  { textAlign: 'center', color: 'var(--text-muted)', padding: '60px 20px', fontSize: 14 },
    resultCard:{ margin: 20, background: 'var(--bg-card)', borderRadius: 14, border: '2px solid #22d3ee', padding: 20 },
    resultTtl: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' },
    credBox:   { background: 'var(--bg-page)', borderRadius: 8, padding: '12px 14px',
                 fontFamily: 'monospace', fontSize: 20, fontWeight: 700, letterSpacing: '0.1em',
                 color: '#22d3ee', textAlign: 'center', margin: '12px 0', wordBreak: 'break-all' },
    copyBtn:   { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                 width: '100%', padding: '10px 0', borderRadius: 8,
                 background: copied ? '#dcfce7' : '#0f172a', color: copied ? '#166534' : '#22d3ee',
                 fontWeight: 700, fontSize: 14, border: '1px solid #22d3ee', cursor: 'pointer' },
    redemption:{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 },
  };

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <h1 style={styles.heroTitle}>{result ? 'Purchase Complete' : 'Confirm Purchase'}</h1>
        {!result && <p style={styles.heroSub}>Review the details below before you pay.</p>}
      </div>

      {loading ? (
        <div style={styles.emptyMsg}>Loading…</div>
      ) : notFound ? (
        <div style={styles.emptyMsg}>
          <p>This package isn't available anymore.</p>
          <Link to="/internet" style={{ color: '#22d3ee' }}>← Back to Internet Packages</Link>
        </div>
      ) : result ? (
        result.purchase?.providerStatus === 'awaiting_activation' && !result.credential ? (
          <div style={styles.resultCard}>
            <p style={styles.resultTtl}>Payment Received</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 4px' }}>
              {result.purchase?.packageSnapshot?.name}
            </p>
            <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.5, margin: '12px 0' }}>
              Your package is being activated (usually under 5 minutes). You'll get a notification
              the moment it's live — just connect to DWIFI as usual.
            </p>
            <Link to="/internet" style={{ ...styles.confirmBtn(false), display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' }}>
              Back to Internet Packages
            </Link>
          </div>
        ) : result.purchase?.radiusUsername ? (
          // BUG FIX (2026-09-14): a hotspot package used to deliver a voucher
          // code here and tell the customer to go "redeem" it at the captive
          // portal — a step that always failed with "already used" because
          // the code was finalized the instant this purchase completed. See
          // services/isp/ispPackageService.js's useDirectRadiusActivation.
          <div style={styles.resultCard}>
            <p style={styles.resultTtl}>✅ Payment Successful!</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 4px' }}>
              {result.purchase?.packageSnapshot?.name}
            </p>
            <p style={{ fontSize: 14, color: 'var(--text-primary)', margin: '0 0 12px', fontWeight: 600 }}>
              To connect, enter these details on the WiFi login page:
            </p>
            <div style={{ ...styles.credBox, display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left', fontSize: 15 }}>
              <span>Username: <strong>{result.purchase.radiusUsername}</strong></span>
              <button
                style={{ background: copiedField === 'username' ? '#dcfce7' : '#0f172a', color: copiedField === 'username' ? '#166534' : '#22d3ee', border: '1px solid #22d3ee', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, marginLeft: 8 }}
                onClick={() => copyField('username', result.purchase.radiusUsername)}
              >
                {copiedField === 'username' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div style={{ ...styles.credBox, display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left', fontSize: 15 }}>
              <span>Password: <strong>{result.purchase.radiusPassword}</strong></span>
              <button
                style={{ background: copiedField === 'password' ? '#dcfce7' : '#0f172a', color: copiedField === 'password' ? '#166534' : '#22d3ee', border: '1px solid #22d3ee', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, marginLeft: 8 }}
                onClick={() => copyField('password', result.purchase.radiusPassword)}
              >
                {copiedField === 'password' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <button
              style={styles.copyBtn}
              onClick={() => copyField('both', `Username: ${result.purchase.radiusUsername}\nPassword: ${result.purchase.radiusPassword}`)}
            >
              {copiedField === 'both' ? <Check /> : <Copy />}
              {copiedField === 'both' ? 'Copied!' : 'Copy Both'}
            </button>
            <p style={styles.redemption}>
              <strong>How to use:</strong><br/>
              Connect to DWIFI WiFi. If a login page appears, enter this username and password to get online.
            </p>
            <Link to="/internet" style={{ ...styles.confirmBtn(false), display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box', marginTop: 16 }}>
              Back to Internet Packages
            </Link>
          </div>
        ) : (
          <div style={styles.resultCard}>
            <p style={styles.resultTtl}>Your Internet Voucher</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 4px' }}>
              {result.purchase?.packageSnapshot?.name}
            </p>
            <div style={styles.credBox}>{result.credential}</div>
            <button style={styles.copyBtn} onClick={copyCredential}>
              {copied ? <Check /> : <Copy />}
              {copied ? 'Copied!' : 'Copy Code'}
            </button>
            {result.purchase?.packageSnapshot?.redemptionInstructions && (
              <p style={styles.redemption}>
                <strong>How to use:</strong><br/>
                {result.purchase.packageSnapshot.redemptionInstructions}
              </p>
            )}
            <Link to="/internet" style={{ ...styles.confirmBtn(false), display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box', marginTop: 16 }}>
              Back to Internet Packages
            </Link>
          </div>
        )
      ) : (
        <div style={styles.body}>
          <p style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>{pkg.name}</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px' }}>
            {pkg.durationLabel} &nbsp;·&nbsp; UGX {fmt(pkg.priceUGX)}
          </p>

          {balance !== null && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
              Wallet balance:&nbsp;
              <strong style={{ color: balance >= pkg.priceUGX ? '#22d3ee' : '#ef4444' }}>
                UGX {fmt(balance)}
              </strong>
              {balance < pkg.priceUGX && (
                <span style={{ color: '#ef4444' }}> — Insufficient balance</span>
              )}
            </div>
          )}

          {needsIspIdentity && (
            <>
              <label style={styles.label}>Your DWIFI username/ticket number</label>
              <input
                style={styles.input}
                type="text"
                placeholder="e.g. ORANGE1-1234"
                value={ispIdentity}
                onChange={e => setIspIdentity(e.target.value)}
              />
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '-6px 0 12px' }}>
                We'll remember this for all future renewals — you won't be asked again.
              </p>
            </>
          )}

          <label style={styles.label}>Phone for SMS (optional)</label>
          <input
            style={styles.input}
            type="tel"
            placeholder="+256 7XX XXX XXX"
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />

          {err && <div style={styles.errBox}>{err}</div>}

          <button
            style={styles.confirmBtn(buying || (balance !== null && balance < pkg.priceUGX) || (needsIspIdentity && !ispIdentity.trim()))}
            onClick={handleBuy}
            disabled={buying || (balance !== null && balance < pkg.priceUGX) || (needsIspIdentity && !ispIdentity.trim())}
          >
            {buying ? 'Processing…' : `Confirm — UGX ${fmt(pkg.priceUGX)}`}
          </button>
          <Link to="/internet" style={styles.backLink}>Cancel</Link>
        </div>
      )}
    </div>
  );
}
